// Install Desktop Apps: opened from the account menu in every app. Shows the installers for this
// computer first and every other platform under "Other Platforms", only for the apps the person's
// plan includes. A platform with no build says "Coming soon"; it never links to a missing file.
//
// A native <dialog> shown modally, so it sits in the top layer above every app's own stacking
// (the top bar is z-40, editors have their own) without a portal. Esc closes it.

import { useEffect, useId, useRef, useState } from "react";
import { Download, Monitor, X } from "@fg/icons";
import {
    OS_LABELS,
    appsForPlan,
    checksumUrl,
    detectPlatform,
    downloadUrl,
    installLayout,
    readPlatformHints,
    type BuildChoice,
    type DesktopApp,
    type Platform,
    type PlatformSection
} from "./desktop-apps";
import { useEnabledApps } from "./enabled-apps";
import { SuiteAppTile } from "./suite-shell";
import type { SuiteAppId } from "./suite-apps";

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(" ");

const TILES: Record<DesktopApp["id"], readonly SuiteAppId[]> = {
    productivity: ["mail", "connect", "sheets", "docs", "slides"],
    mail: ["mail"],
    meeting: ["connect"],
    workbook: ["sheets"],
    notes: ["docs"],
    present: ["slides"]
};

function AppTiles({ app, size }: { app: DesktopApp; size: number }) {
    const tiles = TILES[app.id];
    if (tiles.length === 1) return <SuiteAppTile app={tiles[0] as SuiteAppId} size={size} />;
    // The suite: its apps' tiles, overlapped.
    return (
        <span aria-hidden className="flex shrink-0">
            {tiles.map((t, i) => (
                <span key={t} className={i ? "-ml-2.5" : undefined}>
                    <SuiteAppTile app={t} size={Math.round(size * 0.72)} />
                </span>
            ))}
        </span>
    );
}

function platformLabel(p: Platform): string {
    if (p.os === "other") return "";
    if (p.os === "macos" || p.arch === "unknown") return OS_LABELS[p.os];
    if (p.os === "windows" && p.arch === "arm64") return "Windows on Arm";
    return `${OS_LABELS[p.os]} (${p.arch === "x64" ? "x64" : "Arm64"})`;
}

function DownloadButton({ choice, appName, compact }: { choice: BuildChoice; appName: string; compact?: boolean }) {
    return (
        <a
            href={downloadUrl(choice.build)}
            aria-label={`Download ${appName} for ${OS_LABELS[choice.build.os]}`}
            className={cx(
                "inline-flex items-center justify-center gap-1.5 rounded-md font-semibold outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                compact
                    ? "border px-2.5 py-1 text-xs hover:bg-accent"
                    : "bg-primary px-3.5 py-2 text-sm text-primary-foreground hover:bg-primary/90"
            )}
        >
            <Download className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} aria-hidden />
            Download
        </a>
    );
}

function FileLine({ choice }: { choice: BuildChoice }) {
    return (
        <span className="text-xs text-muted-foreground">
            <span className="break-all">{choice.build.file}</span>
            {" · "}
            <a href={checksumUrl(choice.build)} className="underline-offset-2 hover:underline">
                SHA-256
            </a>
        </span>
    );
}

function ComingSoon() {
    return <span className="text-sm font-medium text-muted-foreground">Coming soon</span>;
}

/** This computer's platform: one card per app. */
function CurrentPlatform({ section, platform }: { section: PlatformSection; platform: Platform }) {
    return (
        <section aria-labelledby="install-current" className="space-y-3">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h2 id="install-current" className="text-lg font-semibold">
                    For This Computer
                </h2>
                <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Monitor className="h-4 w-4" aria-hidden />
                    {platformLabel(platform)}
                </span>
            </div>
            <ul className="grid gap-3 sm:grid-cols-2">
                {section.entries.map(({ app, choice }) => (
                    <li
                        key={app.id}
                        className={cx(
                            "flex flex-col gap-3 rounded-lg border bg-card p-4 text-card-foreground",
                            app.id === "productivity" && "sm:col-span-2"
                        )}
                    >
                        <div className="flex items-start gap-3">
                            <AppTiles app={app} size={40} />
                            <div className="min-w-0">
                                <h3 className="font-semibold">{app.name}</h3>
                                <p className="text-sm text-muted-foreground">{app.blurb}</p>
                            </div>
                        </div>
                        <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1.5">
                            {choice ? (
                                <>
                                    <DownloadButton choice={choice} appName={app.name} />
                                    <FileLine choice={choice} />
                                </>
                            ) : (
                                <ComingSoon />
                            )}
                        </div>
                        {choice?.emulated ? (
                            <p className="text-xs text-muted-foreground">Built for x64. Windows 11 on Arm runs it through emulation.</p>
                        ) : null}
                    </li>
                ))}
            </ul>
        </section>
    );
}

/** Another platform: a compact row per app. */
function OtherPlatform({ section }: { section: PlatformSection }) {
    const headingId = `install-${section.os}`;
    return (
        <section aria-labelledby={headingId}>
            <h3 id={headingId} className="mb-1.5 text-sm font-semibold">
                {OS_LABELS[section.os]}
            </h3>
            <ul className="divide-y rounded-lg border">
                {section.entries.map(({ app, choice }) => (
                    <li key={app.id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2.5">
                        <AppTiles app={app} size={24} />
                        <span className="min-w-0 flex-1 text-sm font-medium">{app.name}</span>
                        {choice ? (
                            <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                <FileLine choice={choice} />
                                <DownloadButton choice={choice} appName={app.name} compact />
                            </span>
                        ) : (
                            <ComingSoon />
                        )}
                    </li>
                ))}
            </ul>
        </section>
    );
}

export function InstallDesktopAppsPage({ onClose }: { onClose: () => void }) {
    const dialog = useRef<HTMLDialogElement>(null);
    const titleId = useId();
    const plan = useEnabledApps();
    const [platform, setPlatform] = useState<Platform | null>(null);

    useEffect(() => {
        const d = dialog.current;
        if (d && !d.open && typeof d.showModal === "function") d.showModal();
    }, []);

    useEffect(() => {
        let live = true;
        void readPlatformHints().then((h) => {
            if (live) setPlatform(detectPlatform(h));
        });
        return () => {
            live = false;
        };
    }, []);

    const apps = plan.status === "ready" ? appsForPlan(plan.apps) : [];
    const layout = platform && plan.status === "ready" ? installLayout(apps, platform) : null;

    return (
        <dialog
            ref={dialog}
            aria-labelledby={titleId}
            onCancel={(e) => {
                e.preventDefault();
                onClose();
            }}
            className="fixed inset-0 m-0 h-full max-h-none w-full max-w-none overflow-y-auto bg-background p-0 text-foreground backdrop:bg-black/40"
        >
            <div className="sticky top-0 z-10 flex h-12 items-center gap-2 border-b bg-background/95 px-3 backdrop-blur sm:px-6">
                <Download className="h-5 w-5" aria-hidden />
                <h1 id={titleId} className="flex-1 truncate text-base font-semibold">
                    Install Desktop Apps
                </h1>
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    title="Close"
                    className="inline-flex h-9 w-9 items-center justify-center rounded hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                    <X className="h-5 w-5" aria-hidden />
                </button>
            </div>

            <div className="mx-auto max-w-4xl space-y-8 px-4 py-6 sm:px-6 sm:py-8">
                <p className="max-w-2xl text-sm text-muted-foreground">
                    Your Fowlguard apps as fast native apps on your computer. These are the apps your plan includes.
                </p>

                {!layout ? (
                    <p role="status" className="text-sm text-muted-foreground">
                        Checking your plan…
                    </p>
                ) : apps.length === 0 ? (
                    <p role="status" className="rounded-lg border p-4 text-sm">
                        Your plan does not include any desktop apps yet. Ask your administrator to add them.
                    </p>
                ) : (
                    <>
                        {layout.current && platform ? (
                            <CurrentPlatform section={layout.current} platform={platform} />
                        ) : (
                            <p className="rounded-lg border bg-muted/50 p-4 text-sm">
                                Desktop apps install on Windows, macOS and Linux. Open this page on your computer to
                                install them, or pick one below.
                            </p>
                        )}
                        <section aria-labelledby="install-others" className="space-y-4">
                            <h2 id="install-others" className="text-lg font-semibold">
                                Other Platforms
                            </h2>
                            {layout.others.map((s) => (
                                <OtherPlatform key={s.os} section={s} />
                            ))}
                        </section>
                    </>
                )}
            </div>
        </dialog>
    );
}
